import { GoogleGenAI } from "@google/genai";
import { ModelId } from "../types";

// The key comes only from REACT_APP_GEMINI_API_KEY (see .env.example); without it requests fail.
const getAiClient = () => new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY });

interface GeminiResponse {
  base64?: string;
  mimeType?: string;
  text?: string;
  usage?: {
    promptTokens: number;
    candidateTokens: number;
    totalTokens: number;
  };
}

/**
 * Sends an image and a prompt to the Gemini model.
 */
export const editImageWithGemini = async (
  base64Image: string,
  mimeType: string,
  prompt: string,
  returnType: 'image' | 'text' = 'image',
  modelId: ModelId = 'gemini-2.5-flash-image',
  config: any = {} // Allow passing specific model config
): Promise<GeminiResponse> => {
  try {
    const ai = getAiClient();
    console.log(`Using model: ${modelId} for output type: ${returnType}`);

    // --- GEMINI (FLASH / PRO) HANDLING ---
    // Both 2.5 Flash and 3.0 Pro support generateContent with image inputs

    const response = await ai.models.generateContent({
      model: modelId,
      contents: {
        parts: [
          {
            inlineData: {
              mimeType: mimeType,
              data: base64Image,
            },
          },
          {
            text: prompt,
          },
        ],
      },
      config: config, // Pass the config (e.g. imageSize for Pro)
    });

    if (!response.candidates || response.candidates.length === 0) {
      throw new Error("No candidates returned from Gemini API.");
    }

    // Extract usage metadata
    const usageMetadata = response.usageMetadata;
    const usage = usageMetadata ? {
      promptTokens: usageMetadata.promptTokenCount || 0,
      candidateTokens: usageMetadata.candidatesTokenCount || 0,
      totalTokens: usageMetadata.totalTokenCount || 0,
    } : undefined;

    const content = response.candidates[0].content;

    // IF expecting TEXT
    if (returnType === 'text') {
      const text = response.text;
      if (!text) {
         throw new Error("Model did not return text as requested.");
      }
      return { text, usage };
    }
    
    // IF expecting IMAGE (Default)
    let resultImageBase64 = '';
    let resultMimeType = 'image/png'; // Default fallback
    
    if (content && content.parts) {
      for (const part of content.parts) {
        if (part.inlineData && part.inlineData.data) {
          resultImageBase64 = part.inlineData.data;
          // Capture the mimetype returned by the model if available
          if (part.inlineData.mimeType) {
            resultMimeType = part.inlineData.mimeType;
          }
          break; // Found the image
        }
      }
    }

    if (!resultImageBase64) {
      // If no image is found, check for text to provide a better error
      const textPart = content?.parts?.find(p => p.text);
      if (textPart) {
        throw new Error(`Model returned text instead of image: ${textPart.text}`);
      }
      throw new Error("Model did not return a valid image.");
    }

    return {
      base64: resultImageBase64,
      mimeType: resultMimeType,
      usage
    };

  } catch (error: any) {
    console.error("Gemini API Error:", error);
    throw new Error(error.message || "Failed to process request with Gemini.");
  }
};